import { Table } from 'antd'
import { CurrencyWithoutUnit, DateTime, numberRenderer } from '$utils/i18n'
import PartNo from './PartNo'
import TextHighlighter from '$components/TextHighlighter'
import TruncateText from '$components/TruncateText'
import { PinIcon } from '$components/Icons'
import Tooltip from '$components/Tooltip'
import { useTheme } from 'styled-components'

const FeasibilityStatusIcon = ({ t, part, materialsById }) => {
  const fcResults = part.dwh_materials_fc_check
  const invalidationReason = part.fc_invalidation_reason
  const theme = useTheme()

  // Case 1: FC has never been run.
  if (fcResults === null || fcResults === undefined) {
    return (
      <Tooltip title={t('fc_not_checked')}>
        <div
          className="traffic-light"
          style={{ width: '12px', margin: '2px 8px -1px 0', opacity: 0.7 }}
        >
          <svg viewBox="0 0 12 12">
            <circle
              cx="6"
              cy="6"
              r="4"
              stroke="grey"
              strokeWidth="1"
              fill="none"
            />
          </svg>
        </div>
      </Tooltip>
    )
  }

  // Case 2: FC is invalid due to a direct action.
  if (typeof fcResults === 'object' && Object.keys(fcResults).length === 0) {
    let tooltipKey = 'fc_invalidated_data_changed'
    if (invalidationReason === 'mapping_changed') {
      tooltipKey = 'fc_invalidated_mapping_changed'
    } else if (invalidationReason === 'client_material_archived') {
      tooltipKey = 'fc_invalidated_client_material_archived'
    }
    return (
      <Tooltip title={t(tooltipKey)}>
        <div
          className="traffic-light"
          style={{ width: '12px', margin: '2px 8px -1px 0' }}
        >
          <svg viewBox="0 0 12 12">
            <circle cx="6" cy="6" r="4" fill="grey" />
          </svg>
        </div>
      </Tooltip>
    )
  }

  // Case 3: FC has results, check against archived status.
  const feasibleMaterialIds = Object.entries(fcResults)
    .filter(([, isFeasible]) => isFeasible)
    .map(([materialId]) => materialId)

  if (feasibleMaterialIds.length === 0) {
    // ORANGE: No combinations were printable.
    return (
      <Tooltip title={t('fc_not_feasible_any_combo')}>
        <div
          className="traffic-light"
          style={{ width: '12px', margin: '2px 8px -1px 0' }}
        >
          <svg viewBox="0 0 12 12">
            <circle
              cx="6"
              cy="6"
              r="4"
              fill={theme.colorBrightYellow500 ?? '#F1B44C'}
            />
          </svg>
        </div>
      </Tooltip>
    )
  }

  // Check if all feasible materials have been archived.
  const areAllFeasibleMaterialsArchived = feasibleMaterialIds.every((id) => {
    const material = materialsById?.[id]
    return material && material.is_archived
  })

  if (areAllFeasibleMaterialsArchived) {
    // GREY: All feasible options were archived.
    return (
      <Tooltip title={t('tooltip.fc_invalidated_dwh_material_archived')}>
        <div
          className="traffic-light"
          style={{ width: '12px', margin: '2px 8px -1px 0' }}
        >
          <svg viewBox="0 0 12 12">
            <circle cx="6" cy="6" r="4" fill="grey" />
          </svg>
        </div>
      </Tooltip>
    )
  } else {
    // GREEN: At least one feasible material is not archived.
    return (
      <Tooltip title={t('fc_feasible')}>
        <div
          className="traffic-light"
          style={{ width: '12px', margin: '2px 8px -1px 0' }}
        >
          <svg viewBox="0 0 12 12">
            <circle
              cx="6"
              cy="6"
              r="4"
              fill={theme.colorBrightGreen500 ?? '#34C38F'}
            />
          </svg>
        </div>
      </Tooltip>
    )
  }
}

export const getDefaultColumnDefinitions = ({ t, filters, theme }) => ({
  feasible: {
    title: 'Feasible',
    className: 'feas-col',
    width: '7%',
    render: (part, _record, _index, materialsById) => (
      <FeasibilityStatusIcon t={t} part={part} materialsById={materialsById} />
    ),
  },
  part_id: {
    title: t('Part No'),
    dataIndex: 'id_part_client',
    sorter: true,
    width: '160px',
    render: (_id, part) => (
      <PartNo part={part} filters={filters.id_part_client} />
    ),
    filter: {
      parameter: 'id_part_client',
      operators: ['contains'],
    },
  },
  name: {
    title: t('Part Name'),
    dataIndex: 'name',
    sorter: true,
    render: (name) => (
      <TruncateText
        text={<TextHighlighter text={name} filters={filters.name} />}
      />
    ),
    filter: {
      parameter: 'name',
      operators: ['contains'],
    },
    ellipsis: true,
  },
  width: {
    title: 'Width',
    unit: 'mm',
    sorter: true,
    dataIndex: 'Width',
    width: '6%',
    render: (_, part) =>
      numberRenderer(t)(
        part.float_attributes.find(({ key }) => key === 'Width')?.value,
      ),
    filter: {
      parameter: 'Width',
      operators: ['=', '>=', '>', '<=', '<'],
      parse: (val) => parseFloat(val),
    },
  },
  depth: {
    title: 'Depth',
    unit: 'mm',
    sorter: true,
    dataIndex: 'Depth',
    width: '6%',
    render: (_, part) =>
      numberRenderer(t)(
        part.float_attributes.find(({ key }) => key === 'Depth')?.value,
      ),
    filter: {
      parameter: 'Depth',
      operators: ['=', '>=', '>', '<=', '<'],
      parse: (val) => parseFloat(val),
    },
  },
  height: {
    title: 'Height',
    unit: 'mm',
    sorter: true,
    dataIndex: 'Height',
    width: '6%',
    render: (_, part) =>
      numberRenderer(t)(
        part.float_attributes.find(({ key }) => key === 'Height')?.value,
      ),
    filter: {
      parameter: 'Height',
      operators: ['=', '>=', '>', '<=', '<'],
      parse: (val) => parseFloat(val),
    },
  },
  dimensions: {
    title: 'Dimensions',
    unit: 'mm',
    width: '6%',
    render: (part) => (
      <div>
        {part.float_attributes.find(({ key }) => key === 'Width')?.value}{' '}
        &times;{' '}
        {part.float_attributes.find(({ key }) => key === 'Height')?.value}{' '}
        &times;{' '}
        {part.float_attributes.find(({ key }) => key === 'Depth')?.value}
      </div>
    ),
  },
  weight: {
    title: 'Weight',
    unit: 'g',
    sorter: true,
    dataIndex: 'Weight',
    width: '6%',
    render: (_, part) =>
      numberRenderer(t)(
        part.float_attributes.find(({ key }) => key === 'Weight')?.value,
      ),
    filter: {
      parameter: 'Weight',
      operators: ['=', '>=', '>', '<=', '<'],
      parse: (val) => parseFloat(val),
    },
  },
  material: {
    title: 'Material',
    dataIndex: 'material',
    sorter: true,
    render: (_, part) => {
      const isCMArchived =
        part.client_material && part.client_material.is_archived
      return (
        <TruncateText
          text={part?.client_material?.name}
          style={isCMArchived ? { color: '#d0b4b4ff' } : {}}
          addedTooltip={
            isCMArchived ? t('parts_master_data.material_archived') : ''
          }
        />
      )
    },
    filter: {
      parameter: 'Material',
      operator: '=',
    },
    ellipsis: true,
  },
  price: {
    title: 'Price',
    unit: '€',
    sorter: true,
    dataIndex: 'Price',
    width: '8%',
    render: (_, part) => (
      <CurrencyWithoutUnit
        value={part.float_attributes.find(({ key }) => key === 'Price')?.value}
      />
    ),
  },
  price_am: {
    title: 'Price AM',
    unit: '€',
    sorter: true,
    dataIndex: 'Price AM',
    width: '8%',
    render: (_, part) => {
      const isMaterialArchived = part?.client_material?.is_archived || false
      const isPriceAMOld = part.float_attributes
        .find(({ key }) => key === 'Price AM')
        ?.description?.includes('old')
      return (
        <CurrencyWithoutUnit
          value={
            part.float_attributes.find(({ key }) => key === 'Price AM')?.value
          }
          style={
            isPriceAMOld ? { color: theme.colorBrightRed500 ?? 'red' } : {}
          }
          tooltipText={
            isPriceAMOld
              ? isMaterialArchived
                ? t('parts_master_data.material_archived_priceam')
                : t('parts_master_data.material_changed_priceam')
              : ''
          }
        />
      )
    },
  },
  created_at: {
    title: 'Creation Date',
    dataIndex: 'created_at',
    sorter: true,
    width: '10%',
    render: (created_at) => <DateTime value={created_at} withoutTime={true} />,
  },
  updated_at: {
    title: 'Modification Date',
    dataIndex: 'updated_at',
    sorter: true,
    width: '10%',
    render: (updated_at) => <DateTime value={updated_at} withoutTime={true} />,
  },
  client_material_id: {
    title: 'Client Material',
    dataIndex: 'client_material_id',
    sorter: true,
    render: (_, part) => {
      return part.client_material?.name || 'No Material'
    },
    filter: {
      parameter: 'client_material_id',
      operators: ['='],
    },
    ellipsis: true,
  },
})

export const getColumnsArray = (
  t,
  requestedColumns,
  columnDefinitions,
  sorting = {},
  selectable = false,
  setReferencePartId = undefined,
  referencePartId = null,
  materialsById,
) => {
  let definitions = []

  if (setReferencePartId && typeof setReferencePartId === 'function') {
    definitions.push({
      title: '',
      dataIndex: 'vector8',
      width: '32px',
      align: 'center',
      sorter: sorting.key === 'vector8',
      sortOrder: sorting.key === 'vector8' ? 'ascend' : null,
      sortDirections: ['ascend'],
      className: 'vector8',
      render: (_, part) => {
        const isPinned = part.isPinned || part.id === referencePartId
        const isCalculating =
          (part.vector8_finished_at === null ||
            part.vector4_finished_at === null) &&
          (part.vector4_started_at !== null || part.vector8_started_at !== null)
        return (
          <PinIcon
            isCalculating={isCalculating}
            isPinned={isPinned}
            disabled={!part.vector8}
            onClick={(pin) => {
              if (pin) {
                setReferencePartId(part.id)
              } else {
                setReferencePartId(null)
              }
            }}
          />
        )
      },
    })
  }

  if (selectable) {
    definitions.push(Table.SELECTION_COLUMN)
  }

  for (let requestedColumn of requestedColumns) {
    if (typeof requestedColumn === 'string') {
      if (requestedColumn in columnDefinitions) {
        const { unit, title, customHeader, render, ...definition } =
          columnDefinitions[requestedColumn]

        if (customHeader) {
          definition.title = customHeader(t)
        } else {
          definition.title = unit ? `${t(title)} (${unit})` : t(title)
        }

        if (sorting.key === definition.dataIndex) {
          definition.sortOrder = sorting.order === 1 ? 'ascend' : 'descend'
        } else {
          definition.sortOrder = null
        }
        if (render) {
          definition.render = (text, record, index) =>
            render(text, record, index, materialsById)
        }

        definitions.push(definition)
      } else {
        console.error(`Unknown column: ${requestedColumn}`)
      }
    }
  }

  return definitions
}
